import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Check, X, Trash2, Edit3, Star, Hourglass, ArrowLeft, Loader2, Filter,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    adminListTestimonials, adminUpdateTestimonial, adminDeleteTestimonial,
    formatApiError,
} from "@/lib/api";

/**
 * Admin-only testimonials moderation queue.
 *
 * Filters by status (pending / approved / rejected / all) with counts. Each
 * row has inline Approve / Reject / Delete actions plus an Edit drawer
 * that opens to tweak quote, name, location and rating before publishing.
 */

const STATUS_TABS = [
    { key: "pending", label: "Pending review" },
    { key: "approved", label: "Published" },
    { key: "rejected", label: "Rejected" },
    { key: "all", label: "All" },
];

function StatusChip({ status }) {
    if (status === "approved") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[var(--vm-sage)]/15 text-[var(--vm-sage)] text-[10px] uppercase tracking-wider font-semibold" data-testid={`status-${status}`}>
                <Check className="w-3 h-3" strokeWidth={2.5} /> Approved
            </span>
        );
    }
    if (status === "rejected") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[var(--vm-secondary)]/15 text-[var(--vm-secondary)] text-[10px] uppercase tracking-wider font-semibold" data-testid={`status-${status}`}>
                <X className="w-3 h-3" strokeWidth={2.5} /> Rejected
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[var(--vm-accent)]/15 text-[var(--vm-accent)] text-[10px] uppercase tracking-wider font-semibold" data-testid={`status-${status}`}>
            <Hourglass className="w-3 h-3" strokeWidth={2} /> Pending
        </span>
    );
}

function Stars({ n }) {
    if (!n) return <span className="text-[var(--vm-text-muted)] text-xs">—</span>;
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${i <= n ? "fill-[var(--vm-accent)] text-[var(--vm-accent)]" : "text-[var(--vm-border)]"}`}
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );
}

function RowEditor({ row, onCancel, onSaved }) {
    const [quote, setQuote] = useState(row.quote || "");
    const [name, setName] = useState(row.name || "");
    const [location, setLocation] = useState(row.location || "");
    const [rating, setRating] = useState(row.rating ?? null);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const patch = {
                quote: quote.trim(),
                name: name.trim() || null,
                location: location.trim() || null,
                rating: rating || null,
            };
            const saved = await adminUpdateTestimonial(row.id, patch);
            onSaved(saved);
            toast.success("Testimonial updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 p-4 rounded-lg bg-[var(--vm-input)]/30 border border-[var(--vm-border)] space-y-3" data-testid={`editor-${row.id}`}>
            <div className="grid sm:grid-cols-2 gap-3">
                <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Display name"
                    data-testid={`editor-name-${row.id}`}
                    className="bg-[var(--vm-bg)] border-[var(--vm-border)]"
                />
                <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Garden location"
                    data-testid={`editor-location-${row.id}`}
                    className="bg-[var(--vm-bg)] border-[var(--vm-border)]"
                />
            </div>
            <Textarea
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                rows={3}
                data-testid={`editor-quote-${row.id}`}
                className="bg-[var(--vm-bg)] border-[var(--vm-border)]"
            />
            <div className="flex items-center gap-1">
                <span className="text-xs text-[var(--vm-text-muted)] mr-2">Rating:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => setRating(rating === n ? null : n)}
                        className="p-1"
                    >
                        <Star
                            className={`w-4 h-4 ${rating && n <= rating ? "fill-[var(--vm-accent)] text-[var(--vm-accent)]" : "text-[var(--vm-border)]"}`}
                        />
                    </button>
                ))}
            </div>
            <div className="flex gap-2">
                <Button
                    onClick={save}
                    disabled={saving || quote.trim().length < 20}
                    data-testid={`editor-save-${row.id}`}
                    className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
                    Save changes
                </Button>
                <Button variant="ghost" onClick={onCancel} data-testid={`editor-cancel-${row.id}`}>
                    Cancel
                </Button>
            </div>
        </div>
    );
}

export default function AdminTestimonials() {
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async (status = tab) => {
        setLoading(true);
        try {
            const r = await adminListTestimonials(status === "all" ? null : status);
            setRows(r);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

    const counts = useMemo(() => {
        const m = { pending: 0, approved: 0, rejected: 0 };
        rows.forEach((r) => { if (m[r.status] !== undefined) m[r.status] += 1; });
        return m;
    }, [rows]);

    const setStatus = async (id, status) => {
        try {
            await adminUpdateTestimonial(id, { status });
            toast.success(
                status === "approved" ? "Published to the About page."
                    : status === "rejected" ? "Rejected — hidden from public."
                        : "Moved back to pending.");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Permanently delete this testimonial?")) return;
        try {
            await adminDeleteTestimonial(id);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-8" data-testid="admin-testimonials-page">
            <PageHeader
                overline="Admin"
                title="Testimonials review"
                description="Approve, reject or edit user-submitted stories before they appear on the About page."
            />

            <div className="flex flex-wrap items-center gap-2">
                <Link to="/admin">
                    <Button variant="ghost" data-testid="back-to-admin" className="text-[var(--vm-text-muted)]">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Users
                    </Button>
                </Link>
                <div className="flex items-center gap-1 ml-auto">
                    <Filter className="w-4 h-4 text-[var(--vm-sage)] mr-1" />
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`tab-${t.key}`}
                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${
                                tab === t.key
                                    ? "bg-[var(--vm-accent)] text-[var(--vm-bg)] border-[var(--vm-accent)]"
                                    : "border-[var(--vm-border)] text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)]"
                            }`}
                        >
                            {t.label}
                            {t.key !== "all" && counts[t.key] > 0 && (
                                <span className="ml-1.5 opacity-75">({counts[t.key]})</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-[var(--vm-text-muted)] py-12 text-center">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="journal-card p-10 text-center text-[var(--vm-text-muted)]" data-testid="empty-state">
                    Nothing here yet — no testimonials match this filter.
                </div>
            ) : (
                <div className="space-y-4">
                    {rows.map((row) => (
                        <article key={row.id} className="journal-card p-5" data-testid={`testimonial-row-${row.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-[var(--vm-text)]" data-testid={`row-name-${row.id}`}>
                                            {row.name || "Anonymous"}
                                        </span>
                                        <StatusChip status={row.status} />
                                        {row.is_starter && (
                                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--vm-input)] text-[var(--vm-text-muted)]">
                                                Starter
                                            </span>
                                        )}
                                    </div>
                                    {row.location && (
                                        <div className="text-xs text-[var(--vm-text-muted)] mt-0.5">{row.location}</div>
                                    )}
                                    <p className="text-sm text-[var(--vm-text-secondary)] italic mt-2 leading-relaxed" data-testid={`row-quote-${row.id}`}>
                                        "{row.quote}"
                                    </p>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-[var(--vm-text-muted)]">
                                        <Stars n={row.rating} />
                                        <span>Tier: {row.tier || "free"}</span>
                                        <span>Submitted: {row.created_at?.slice(0, 10) || "—"}</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                    {row.status !== "approved" && (
                                        <Button
                                            size="sm"
                                            onClick={() => setStatus(row.id, "approved")}
                                            data-testid={`approve-${row.id}`}
                                            className="rounded-full bg-[var(--vm-sage)] hover:bg-[var(--vm-sage)]/90 text-[var(--vm-bg)]"
                                        >
                                            <Check className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Approve
                                        </Button>
                                    )}
                                    {row.status !== "rejected" && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setStatus(row.id, "rejected")}
                                            data-testid={`reject-${row.id}`}
                                            className="rounded-full border-[var(--vm-border)] text-[var(--vm-text-muted)] hover:text-[var(--vm-secondary)]"
                                        >
                                            <X className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Reject
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditing(editing === row.id ? null : row.id)}
                                        data-testid={`edit-${row.id}`}
                                        className="rounded-full border-[var(--vm-border)]"
                                    >
                                        <Edit3 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Edit
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => remove(row.id)}
                                        data-testid={`delete-${row.id}`}
                                        className="rounded-full text-[var(--vm-text-muted)] hover:text-[var(--vm-secondary)]"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                    </Button>
                                </div>
                            </div>

                            {editing === row.id && (
                                <RowEditor
                                    row={row}
                                    onCancel={() => setEditing(null)}
                                    onSaved={(saved) => {
                                        setEditing(null);
                                        setRows((prev) => prev.map((r) => r.id === saved.id ? saved : r));
                                    }}
                                />
                            )}
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
